// /src/pages/private/members/personalhub/Hangar.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSecurity } from '../../../../SecurityContext';
import usePersonalHangarSession from './hooks/usePersonalHangarSession';
import HangarFilters from './hangar/filters/HangarFilters';
import VehicleCard from './hangar/cards/VehicleCard';
import VehicleListRow from './hangar/cards/VehicleListRow';
import PackageCard from './hangar/cards/PackageCard';
import ItemCard from './hangar/cards/ItemCard';
import ItemListRow from './hangar/cards/ItemListRow';
import { readHangarViewMode, writeHangarViewMode } from './hangar/hangarViewModeStorage';
import { normalizeHangarSize } from './hangar/normalizeHangarSize';

/* ---------- helpers ---------- */

// English comment: build a normalized searchable string for any entity
function buildSearchIndex(obj) {
  if (!obj) return '';

  const values = [];

  const collect = (v) => {
    if (!v) return;
    if (typeof v === 'string') values.push(v.toLowerCase());else
    if (Array.isArray(v)) v.forEach(collect);else
    if (typeof v === 'object') Object.values(v).forEach(collect);
  };

  collect(obj);
  return values.join(' ');
}

// English comment: generic text matcher for prebuilt searchIndex
function matchesSearch(searchIndex, term) {
  if (!term) return true;
  return searchIndex.includes(term.toLowerCase());
}

// English comment: normalize item type to filter key
function normalizeItemType(type) {
  if (!type) return null;

  const t = type.toLowerCase();
  if (t.includes('skin')) return 'skin';
  if (t.includes('fps')) return 'fps';
  if (t.includes('component')) return 'component';
  if (t.includes('upgrade')) return 'upgrades';

  return null;
}

// English comment: detect upgrade item types (standalone RSI upgrade pledges)
function isUpgradeType(type) {
  return normalizeItemType(type) === 'upgrades';
}

// English comment: detect insurance item types
function isInsuranceType(type) {
  if (!type) return false;
  return type.toLowerCase().includes('insurance');
}

function computeHangarFilterAvailability({ vehicles, packages, items, loaners }) {
  const sizeAvailability = {
    vehicle: false,
    snub: false,
    small: false,
    medium: false,
    large: false,
    capital: false,
  };
  const itemTypeAvailability = {
    upgrades: false,
    skin: false,
    fps: false,
    component: false,
  };

  const markSize = (vehicle) => {
    const sizeKey = normalizeHangarSize(vehicle?.size);
    if (sizeKey && Object.prototype.hasOwnProperty.call(sizeAvailability, sizeKey)) {
      sizeAvailability[sizeKey] = true;
    }
  };

  const markItemType = (item) => {
    const typeKey = normalizeItemType(item?.type);
    if (typeKey && typeKey !== 'upgrades') itemTypeAvailability[typeKey] = true;
  };

  const markUpgrade = (item) => {
    if (isUpgradeType(item?.type)) itemTypeAvailability.upgrades = true;
  };

  let standaloneShips = (vehicles || []).length;
  let shipPackages = 0;
  let itemPackages = 0;

  (vehicles || []).forEach(markSize);
  (loaners || []).forEach(markSize);

  for (const pkg of packages || []) {
    const pkgVehicles = Array.isArray(pkg.vehicles) ? pkg.vehicles : [];
    const pkgItems = Array.isArray(pkg.items) ? pkg.items : [];

    if (pkgVehicles.length === 1) {
      standaloneShips += 1;
      markSize(pkgVehicles[0]);
    } else if (pkgVehicles.length > 1) {
      shipPackages += 1;
      pkgVehicles.forEach(markSize);
    } else if (pkgItems.length > 0) {
      itemPackages += 1;
    }

    pkgItems.forEach(markItemType);
    pkgItems.forEach(markUpgrade);
  }

  (items || []).forEach(markItemType);
  (items || []).forEach(markUpgrade);

  const standaloneItems = (items || []).filter((item) => !isUpgradeType(item?.type));

  return {
    standaloneShips: standaloneShips > 0,
    shipPackages: shipPackages > 0,
    loaners: (loaners || []).length > 0,
    itemPackages: itemPackages > 0,
    items: standaloneItems.length > 0,
    upgrades: itemTypeAvailability.upgrades,
    ...sizeAvailability,
    ...itemTypeAvailability,
  };
}

function applyAvailabilityToFilters(prev, availability, keys, previousAvailability = null) {
  const next = { ...prev };
  let changed = false;

  keys.forEach((key) => {
    if (availability[key] === false) {
      if (next[key]) {
        next[key] = false;
        changed = true;
      }
      return;
    }

    if (previousAvailability?.[key] === false && !next[key]) {
      next[key] = true;
      changed = true;
    }
  });

  return changed ? next : prev;
}

function buildFiltersFromAvailability(availability, keys) {
  return Object.fromEntries(keys.map((key) => [key, availability[key] === true]));
}

const SHIP_FILTER_KEYS = ['standaloneShips', 'shipPackages', 'loaners'];
const SIZE_FILTER_KEYS = ['vehicle', 'snub', 'small', 'medium', 'large', 'capital'];
const ITEM_TYPE_FILTER_KEYS = ['itemPackages', 'items', 'upgrades', 'skin', 'fps', 'component'];

/* ---------- main ---------- */

export default function Hangar({ embeddedInPersonalHub = false }) {
  const { subjectHasPermission } = useSecurity();

  const canReadHangar = subjectHasPermission('private.navbar.personalhub.hangar', 'read');

  const {
    hangars,
    allVehicles,
    activeHangarIndex,
    setActiveHangarIndex,
    loading: sessionLoading,
    pageFetching,
    shellVisible,
    error: sessionError,
    refetchHangar,
  } = usePersonalHangarSession({ enabled: canReadHangar });

  const [vehicles, setVehicles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [loaners, setLoaners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => readHangarViewMode());

  const [filters, setFilters] = useState({
    standaloneShips: false,
    shipPackages: false,
    loaners: false,
  });

  const [itemTypeFilters, setItemTypeFilters] = useState({
    itemPackages: false,
    items: false,
    upgrades: false,
    skin: false,
    fps: false,
    component: false,
  });

  const [sizeFilters, setSizeFilters] = useState({
    vehicle: false,
    snub: false,
    small: false,
    medium: false,
    large: false,
    capital: false,
  });

  const loading = sessionLoading && !shellVisible;
  const error = sessionError;
  const isListView = viewMode === 'list';
  const VehicleComponent = isListView ? VehicleListRow : VehicleCard;
  const ItemComponent = isListView ? ItemListRow : ItemCard;
  const vehicleContainerClass = isListView ? 'vehicle-list' : 'vehicle-grid';
  const itemContainerClass = isListView ? 'item-list' : 'item-grid';

  const hangarPageRef = useRef(null);
  const hangarContentRef = useRef(null);
  const filtersSyncedHandleRef = useRef(null);
  const prevFilterAvailabilityRef = useRef(null);

  const canSearch = subjectHasPermission("private.navbar.personalhub.hangar.search", "write");
  const canFilterShips = subjectHasPermission("private.navbar.personalhub.hangar.filters.ships", "write");
  const canFilterSizes = subjectHasPermission("private.navbar.personalhub.hangar.filters.sizes", "write");
  const canFilterItems = subjectHasPermission("private.navbar.personalhub.hangar.filters.items", "write");
  const canFilterItemTypes = subjectHasPermission("private.navbar.personalhub.hangar.filters.item-types", "write");
  const canAddIngame = subjectHasPermission("private.navbar.personalhub.hangar.ingame.add", "create");
  const canRemoveIngame = subjectHasPermission("private.navbar.personalhub.hangar.ingame.remove", "delete");
  const canEditLabel = subjectHasPermission("private.navbar.personalhub.hangar.ship-label", "write");
  const canReadSettingsTools = subjectHasPermission("private.settings.tools", "read");

  const hangarManualAddNote =
    "Note that whenever there is a wipe in Star Citizen, all ships will be erased from the database automatically.";

  // English comment: size ordering for vehicles (frontend merge)
  const SIZE_ORDER = {
    vehicle: 0,
    snub: 1,
    small: 2,
    medium: 3,
    large: 4,
    capital: 5
  };

  // English comment: parse RSI mass string like "715,175.00 Kg" into a number (kg)
  function parseMassKg(raw) {
    if (!raw) return null;

    const s = String(raw).trim();

    // English comment: strip unit and normalize separators
    const cleaned = s.
    replace(/kg/i, '').
    replace(/\s+/g, '').
    replace(/,/g, '');

    const n = Number(cleaned);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  // English comment: sort vehicles by mass first, fallback to size
  function sortVehiclesByMassThenSize(a, b) {
    const aMass = parseMassKg(a.mass);
    const bMass = parseMassKg(b.mass);

    const aHasMass = aMass !== null;
    const bHasMass = bMass !== null;

    // English comment: both have valid mass → sort by mass
    if (aHasMass && bHasMass) return aMass - bMass;

    // English comment: only one has mass → mass first
    if (aHasMass && !bHasMass) return -1;
    if (!aHasMass && bHasMass) return 1;

    // English comment: fallback to size ordering
    const aOrder = SIZE_ORDER[normalizeHangarSize(a.size)] ?? 99;
    const bOrder = SIZE_ORDER[normalizeHangarSize(b.size)] ?? 99;
    return aOrder - bOrder;
  }

  // English comment: check if vehicle size is enabled by filters
  function matchesSizeFilter(size, sizeFiltersLocal) {
    const sizeKey = normalizeHangarSize(size);
    if (!sizeKey) return true;
    return !!sizeFiltersLocal[sizeKey];
  }

  // English comment: keep active index valid when hangars list changes
  useEffect(() => {
    if (!hangars.length) return;
    if (activeHangarIndex >= hangars.length) setActiveHangarIndex(0);
  }, [hangars, activeHangarIndex]);

  useEffect(() => {
    writeHangarViewMode(viewMode);
  }, [viewMode]);

  // English comment: current hangar snapshot
  const currentHangar = hangars[activeHangarIndex] || hangars[0] || null;
  const currentHandleId = currentHangar?.handleId || null;

  const applyHangarToState = (h) => {
    const safe = h || {};
    setVehicles(safe.vehicles || []);
    setPackages(safe.packages || []);
    setItems(safe.items || []);
    setLoaners(safe.loaners || []);
  };

  useEffect(() => {
    const current = hangars[activeHangarIndex] || hangars[0] || null;
    applyHangarToState(current);
  }, [hangars, activeHangarIndex]);

  const filterAvailability = useMemo(
    () =>
      computeHangarFilterAvailability({
        vehicles,
        packages,
        items,
        loaners,
      }),
    [vehicles, packages, items, loaners]
  );

  useEffect(() => {
    if (!currentHandleId) return;

    const previousAvailability = prevFilterAvailabilityRef.current;

    if (filtersSyncedHandleRef.current !== currentHandleId) {
      filtersSyncedHandleRef.current = currentHandleId;
      setFilters(buildFiltersFromAvailability(filterAvailability, SHIP_FILTER_KEYS));
      setSizeFilters(buildFiltersFromAvailability(filterAvailability, SIZE_FILTER_KEYS));
      setItemTypeFilters(buildFiltersFromAvailability(filterAvailability, ITEM_TYPE_FILTER_KEYS));
      prevFilterAvailabilityRef.current = filterAvailability;
      return;
    }

    setFilters((prev) =>
      applyAvailabilityToFilters(prev, filterAvailability, SHIP_FILTER_KEYS, previousAvailability)
    );
    setSizeFilters((prev) =>
      applyAvailabilityToFilters(prev, filterAvailability, SIZE_FILTER_KEYS, previousAvailability)
    );
    setItemTypeFilters((prev) =>
      applyAvailabilityToFilters(prev, filterAvailability, ITEM_TYPE_FILTER_KEYS, previousAvailability)
    );
    prevFilterAvailabilityRef.current = filterAvailability;
  }, [currentHandleId, filterAvailability]);

  const fetchHangar = async (preferredIndex = activeHangarIndex) => {
    await refetchHangar({ preferredIndex });
  };

  const handleRemoveIngameShip = async (ingameId) => {
    if (!canRemoveIngame) return;
    if (!currentHandleId) return;

    await axios.post(
      '/api/user/remove-in-game-ship',
      { id: ingameId, handleId: currentHandleId },
      { withCredentials: true }
    );

    await fetchHangar(activeHangarIndex);
  };

  const handleSaveShipLabel = async ({ handleId, vehicleId, packageName, userLabel, ingameId }) => {
    if (!canEditLabel) return;

    try {
      await axios.post(
        '/api/user/update-hangar-ship-label',
        { handleId, vehicleId, packageName, userLabel, ingameId },
        { withCredentials: true }
      );

      await fetchHangar(activeHangarIndex);
    } catch (error) {
      const message = error?.response?.data?.error || 'Failed to save ship label';
      throw new Error(message);
    }
  };

  const enrichVehicle = (vehicle) => ({
    ...vehicle,
    handleId: currentHandleId,
    onRemove: handleRemoveIngameShip,
  });

  const {
    filteredStandaloneVehicles,
    filteredShipPackages,
    filteredItemPackages
  } = useMemo(() => {
    const mergedVehicles = [];
    const shipPkgs = [];
    const itemPkgs = [];

    vehicles.forEach((v) => {
      const searchIndex = buildSearchIndex(v);

      if (
      matchesSearch(searchIndex, searchTerm) &&
      matchesSizeFilter(v.size, sizeFilters))
      {
        mergedVehicles.push(enrichVehicle({
          ...v,
          searchIndex,
        }));
      }
    });

    for (const pkg of packages) {
      const pkgVehicles = Array.isArray(pkg.vehicles) ? pkg.vehicles : [];
      const pkgItems = Array.isArray(pkg.items) ? pkg.items : [];

      const matchingVehicles = pkgVehicles.filter((v) =>
      matchesSearch(buildSearchIndex(v), searchTerm) &&
      matchesSizeFilter(v.size, sizeFilters)
      );

      if (pkgVehicles.length === 1 && matchingVehicles.length === 1) {
        mergedVehicles.push(enrichVehicle({
          ...matchingVehicles[0],
          skins: pkgItems.filter((i) => normalizeItemType(i.type) === 'skin'),
          insurances: pkgItems.filter((i) => isInsuranceType(i.type)),
          searchIndex: buildSearchIndex(matchingVehicles[0]),
        }));
      } else if (pkgVehicles.length > 1 && matchingVehicles.length > 0) {
        const sortedVehicles = [...matchingVehicles].sort(sortVehiclesByMassThenSize);

        shipPkgs.push({
          ...pkg,
          vehicles: sortedVehicles.map((vehicle) => enrichVehicle(vehicle)),
          searchIndex: buildSearchIndex({ ...pkg, vehicles: sortedVehicles })
        });
      } else {
        if (!itemTypeFilters.itemPackages) continue;

        const filteredPkgItems = pkgItems.filter((i) => {
          if (!matchesSearch(buildSearchIndex(i), searchTerm)) return false;
          const typeKey = normalizeItemType(i.type);
          return !typeKey || itemTypeFilters[typeKey];
        });

        if (filteredPkgItems.length > 0) {
          itemPkgs.push({
            ...pkg,
            items: filteredPkgItems,
            searchIndex: buildSearchIndex({ ...pkg, items: filteredPkgItems })
          });
        }
      }
    }

    mergedVehicles.sort(sortVehiclesByMassThenSize);

    return {
      filteredStandaloneVehicles: mergedVehicles,
      filteredShipPackages: shipPkgs,
      filteredItemPackages: itemPkgs
    };
  }, [vehicles, packages, searchTerm, itemTypeFilters, sizeFilters, currentHandleId]);

  const filteredLoaners = useMemo(
    () =>
    (loaners || []).
    filter((v) =>
    matchesSearch(buildSearchIndex(v), searchTerm) &&
    matchesSizeFilter(v.size, sizeFilters)
    ).
    map((v) => enrichVehicle(v)).
    sort(sortVehiclesByMassThenSize),
    [loaners, searchTerm, sizeFilters]
  );

  const filteredUpgrades = useMemo(
    () =>
    (items || []).filter((i) => {
      if (!isUpgradeType(i.type)) return false;
      return matchesSearch(buildSearchIndex(i), searchTerm);
    }),
    [items, searchTerm]
  );

  const filteredItems = useMemo(
    () =>
    (items || []).filter((i) => {
      if (isUpgradeType(i.type)) return false;
      if (!matchesSearch(buildSearchIndex(i), searchTerm)) return false;
      const typeKey = normalizeItemType(i.type);
      return !typeKey || itemTypeFilters[typeKey];
    }),
    [items, searchTerm, itemTypeFilters]
  );

  const hangarHasNoSyncedData =
    !loading &&
    !error &&
    (hangars.length === 0 ||
      (vehicles.length === 0 &&
        packages.length === 0 &&
        items.length === 0 &&
        loaners.length === 0));

  useEffect(() => {
    if (!canReadHangar || loading || error) return undefined;

    const page = hangarPageRef.current;
    const content = hangarContentRef.current;
    if (!page || !content) return undefined;

    const onWheel = (e) => {
      const maxScroll = Math.max(0, content.scrollHeight - content.clientHeight);
      const next = Math.min(maxScroll, Math.max(0, content.scrollTop + e.deltaY));

      if (content.contains(e.target) && next === content.scrollTop + e.deltaY) {
        return;
      }

      e.preventDefault();
      content.scrollTop = next;
    };

    page.addEventListener('wheel', onWheel, { passive: false });
    return () => page.removeEventListener('wheel', onWheel);
  }, [canReadHangar, loading, error]);

  useEffect(() => {
    if (!embeddedInPersonalHub || !canReadHangar) return undefined;

    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [embeddedInPersonalHub, canReadHangar]);

  if (!canReadHangar) {
    return <p className="no-permission">No access to hangar</p>;
  }

  const rootClassName = embeddedInPersonalHub
    ? 'hangar-page hangar-page--personal-hub'
    : 'private-pages hangar-page';

  return (
    <div
      className={[
        rootClassName,
        pageFetching ? 'hangar-page--page-fetching' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={hangarPageRef}
    >
      {loading ? (
        <p className="member-dashboard__state">Loading hangar…</p>
      ) : null}

      {shellVisible && !error &&
      <div className="hangar-layout">
          <HangarFilters
          hangars={hangars}
          activeHangarIndex={activeHangarIndex}
          setActiveHangarIndex={setActiveHangarIndex}
          applyHangarToState={applyHangarToState}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          sizeFilters={sizeFilters}
          setSizeFilters={setSizeFilters}
          itemTypeFilters={itemTypeFilters}
          setItemTypeFilters={setItemTypeFilters}
          filterAvailability={filterAvailability}
          canSearch={canSearch}
          canFilterShips={canFilterShips}
          canFilterSizes={canFilterSizes}
          canFilterItems={canFilterItems}
          canFilterItemTypes={canFilterItemTypes}
          canAddIngame={canAddIngame}
          viewMode={viewMode}
          setViewMode={setViewMode}
          allVehicles={allVehicles}
          onAddIngameShip={async (vehicleId) => {
            if (!currentHandleId) return;
            await axios.post(
              '/api/user/add-in-game-ship',
              { handleId: currentHandleId, vehicleId },
              { withCredentials: true }
            );
            await fetchHangar(activeHangarIndex);
          }} />
        

          <div className="hangar-content" ref={hangarContentRef}>
            {hangarHasNoSyncedData &&
            <aside
              className="hangar-empty-card"
              aria-labelledby="hangar-empty-title">
              <div className="hangar-empty-card__accent" aria-hidden="true" />
              <div className="hangar-empty-card__body">
                <h3 id="hangar-empty-title" className="hangar-empty-card__title">
                  No hangar data yet
                </h3>
                {canReadSettingsTools ?
                <>
                    <p className="hangar-empty-card__text">
                      Your RSI fleet is not synced. Open{' '}
                      <Link className="hangar-empty-card__link" to="/settings/tools">
                        User Settings → Tool settings
                      </Link>
                      {' '}to install the Chrome extension from there, then use it to connect and import your
                      ships and items.
                    </p>
                    <div className="hangar-empty-card__actions">
                      <Link className="hangar-empty-card__cta" to="/settings/tools">
                        Open Tool settings
                      </Link>
                    </div>
                    <p className="hangar-empty-card__note">
                      Or add your ship manually. {hangarManualAddNote}
                    </p>
                  </> :

                <p className="hangar-empty-card__text">
                    You can add your ship manually. {hangarManualAddNote}
                  </p>
                }
              </div>
            </aside>
            }

            {filters.standaloneShips && filteredStandaloneVehicles.length > 0 &&
          <section className="standalone-section">
                <h3 className="standalone-title">Standalone ships</h3>
                <div className={vehicleContainerClass}>
                  {filteredStandaloneVehicles.map((v, i) =>
              <VehicleComponent
                key={`${v.vehicleName}-${i}`}
                v={v}
                onSaveLabel={handleSaveShipLabel}
                canEditLabel={canEditLabel}
                canRemoveIngame={canRemoveIngame}
              />
              )}
                </div>
              </section>
          }

            {filters.shipPackages && filteredShipPackages.length > 0 &&
          filteredShipPackages.map((pkg) =>
          <PackageCard
            key={pkg.packageName}
            pkg={pkg}
            onSaveLabel={handleSaveShipLabel}
            canEditLabel={canEditLabel}
            canRemoveIngame={canRemoveIngame}
            viewMode={viewMode}
          />
          )}

            {filters.loaners && filteredLoaners.length > 0 &&
          <section className="loaner-section">
                <h3 className="standalone-title">Loaner ships</h3>
                <div className={vehicleContainerClass}>
                  {filteredLoaners.map((v, i) =>
              <VehicleComponent key={`${v.vehicleName}-${i}`} v={v} isLoaner />
              )}
                </div>
              </section>
          }

            {itemTypeFilters.itemPackages && filteredItemPackages.length > 0 &&
          filteredItemPackages.map((pkg) =>
          <PackageCard
            key={pkg.packageName}
            pkg={pkg}
            onSaveLabel={handleSaveShipLabel}
            canEditLabel={canEditLabel}
            canRemoveIngame={canRemoveIngame}
            viewMode={viewMode}
          />
          )}

            {itemTypeFilters.upgrades && filteredUpgrades.length > 0 &&
          <section className="items-section upgrades-section">
                <h3 className="standalone-title">Upgrades</h3>
                <div className={itemContainerClass}>
                  {filteredUpgrades.map((i, idx) =>
              <ItemComponent key={`upgrade-${i.itemId}-${idx}`} item={i} />
              )}
                </div>
              </section>
          }

            {itemTypeFilters.items && filteredItems.length > 0 &&
          <section className="items-section">
                <h3 className="standalone-title">Items</h3>
                <div className={itemContainerClass}>
                  {filteredItems.map((i, idx) =>
              <ItemComponent key={`${i.itemId}-${idx}`} item={i} />
              )}
                </div>
              </section>
          }
          </div>
        </div>
      }

      <div
        className="display-none"
        data-resource="private.navbar.personalhub.hangar.ship-label"
        data-action="write"
        data-route="/api/user/update-hangar-ship-label"
        data-description="Personal hub → Hangar → Ship label" />

      <div
        className="display-none"
        data-resource="private.navbar.personalhub.hangar.ingame.remove"
        data-action="delete"
        data-route="/api/user/remove-in-game-ship"
        data-description="Personal hub → Hangar → Ingame → Remove" />
      
      <div
        className="display-none"
        data-resource="private.navbar.personalhub.hangar"
        data-action="read"
        data-route="/api/user/retrieve-personal-hangar" data-description="Personal hub → Hangar" />
      
      <div
        className="display-none"
        data-resource="private.navbar.personalhub.hangar.ingame.add"
        data-action="read"
        data-route="/api/user/retrieve-all-vehicles" data-description="Personal hub → Hangar → Ingame → Add" />
      
    </div>);

}